import { useEffect, useState } from 'react'
import useServerWar from '../../hooks/useServerWar'
import BattleScreen from '../v2/BattleScreen'
import WarEnd from './WarEnd'
import RulesModal from '../RulesModal'
import { setSoundMuted } from '../../utils/soundManager'
import { usePrototype, warLink } from './PrototypeContext'
import ShareButton from './ShareButton'
import WarClock, { clockLeftMs, mmss } from './WarClock'
import './prototype.css'

const hoursLeft = (ms) => (ms > 3600000 ? `${Math.floor(ms / 3600000)}h` : `${Math.max(1, Math.ceil(ms / 60000))}m`)

// One war the server runs, on the war board (BattleScreen) driven by
// useServerWar. Who it's against sits under Opp in the header, and a sync
// war's chess clocks along its bottom edge (WarClock); a small button under
// You opens the war's menu: the war's terms, the sync clock or
// the async turn's deadline, in an async war that waits on them a link to
// nudge them with, and Rules, sound and Home.
export default function WarScreen({ warId }) {
    const { goHome } = usePrototype()
    const w = useServerWar(warId)
    const [rulesOpen, setRulesOpen] = useState(false)
    const [soundOn, setSoundOn] = useState(true)
    const [menuOpen, setMenuOpen] = useState(false)
    const [now, setNow] = useState(() => Date.now())
    const live = w.war?.status === 'active' && w.war?.mode !== 'bot'
    useEffect(() => {
        if (!live) return undefined
        const t = setInterval(() => setNow(Date.now()), 1000)
        return () => clearInterval(t)
    }, [live])

    if (w.loading) {
        return (
            <div className="pt-screen">
                <p className="pt-dim">Loading the war…</p>
                <button className="lobby-btn-ghost" onClick={goHome}>Home</button>
            </div>
        )
    }

    const { war, seat } = w
    const other = seat === 'p1' ? 'p2' : 'p1'
    const opponent = war.players[other]
    let timing = null
    if (war.status === 'active' && war.mode === 'sync') {
        const mine = clockLeftMs(war, seat, now), theirs = clockLeftMs(war, other, now)
        timing = <span>Your clock <strong className={mine < 60000 ? 'pt-warn' : ''}>{mmss(mine)}</strong> · theirs {mmss(theirs)}</span>
    } else if (war.status === 'active' && war.mode === 'async' && war.deadline && !war.submitted[seat]) {
        timing = <span>Turn due in <strong>{hoursLeft(war.deadline - now)}</strong></span>
    }

    // Async, your orders in, theirs not: send them the war's link.
    const nudge = war.status === 'active' && war.mode === 'async' && war.submitted[seat] && !war.submitted[other]

    const toggleSound = () => setSoundOn(on => { setSoundMuted(on); return !on })

    return (
        <main>
            <RulesModal isVisible={rulesOpen} onClose={() => setRulesOpen(false)} />
            <div className="centered">
                <BattleScreen
                    selfBoard={w.selfBoard}
                    oppBoard={w.oppBoard}
                    selfScore={w.selfScore}
                    oppScore={w.oppScore}
                    currentRound={w.currentRound}
                    isPaired={w.isPaired}
                    canDeploy={w.canDeploy}
                    armiesToDeploy={w.armiesToDeploy}
                    oppArmiesToDeploy={w.oppArmiesToDeploy}
                    isSubmitDisabled={w.isSubmitDisabled}
                    message={w.message}
                    setMessage={w.setMessage}
                    onAction={w.onAction}
                    onSubmit={w.handleSubmit}
                    onReveal={w.handleReveal}
                    isAnimating={w.isAnimating}
                    showingPreResolution={w.showingPreResolution}
                    activeClashId={w.activeClashId}
                    clashResult={w.clashResult}
                    activeClashContext={w.activeClashContext}
                    activeClashWinner={w.activeClashWinner}
                    activeClashSelfStrength={w.activeClashSelfStrength}
                    activeClashOppStrength={w.activeClashOppStrength}
                    activeClashOppTargetIndex={w.activeClashOppTargetIndex}
                    activeClashSelfTargetIndex={w.activeClashSelfTargetIndex}
                    activeClashAbsorbed={w.activeClashAbsorbed}
                    activeClashAimS={w.activeClashAimS}
                    activeClashSelfHitInfo={w.activeClashSelfHitInfo}
                    activeClashSelfHitsLog={w.activeClashSelfHitsLog}
                    warResult={w.warResult}
                    warEnd={<WarEnd warId={warId} seat={w.seat} result={w.warResult} warDoc={w.war} onHome={goHome} />}
                    targetPlots={w.targetPlots}
                    targetSubplots={w.targetSubplots}
                    onTargetPlot={w.onTargetPlot}
                    yieldBalance={w.yieldBalance}
                    onHomefrontAction={w.onHomefrontAction}
                    pendingRockets={w.pendingRockets}
                    canAffordRocket={w.canAffordRocket}
                    pendingDrones={w.pendingDrones}
                    canAffordDrone={w.canAffordDrone}
                    onDroneTarget={w.onDroneTarget}
                    droneFlights={w.droneFlights}
                    baseBuildFx={w.baseBuildFx}
                    selfHf={w.selfHf}
                    oppHf={w.oppHf}
                    selfLost={w.selfLost}
                    oppLost={w.oppLost}
                    onStructureKilled={w.onStructureKilled}
                    structuresToWin={w.structuresToWin}
                    warCapacities={w.warCapacities}
                    dropZones={w.dropZones}
                    requiredDropZoneCount={w.requiredDropZoneCount}
                    onDropZone={w.onDropZone}
                    placingDropZones={w.placingDropZones}
                    resupplyDeliveries={w.resupplyDeliveries}
                    revealOverlayUp={w.revealOverlayUp}
                    oppReadyOverlayUp={w.oppReadyOverlayUp}
                    revealRound={w.currentRound}
                    oppName={opponent.name ?? 'A friend'}
                    headerClock={war.status === 'active' && war.mode === 'sync' ? <WarClock war={war} seat={seat} now={now} /> : null}
                    menuButton={
                        <button className={nudge ? 'pt-war-menu__btn pt-war-menu__btn--due' : 'pt-war-menu__btn'} onClick={() => setMenuOpen(true)} aria-label="War menu">
                            <svg viewBox="0 0 16 4" aria-hidden="true"><circle cx="2" cy="2" r="1.6" /><circle cx="8" cy="2" r="1.6" /><circle cx="14" cy="2" r="1.6" /></svg>
                        </button>
                    }
                />
            </div>
            {menuOpen && (
                // The war's menu, over the war (a tap outside the sheet closes it).
                <div className="pt-war-menu" onClick={() => setMenuOpen(false)}>
                    <div className="pt-war-menu__sheet" onClick={(e) => e.stopPropagation()}>
                        <div className="pt-war-strip">
                            <span>vs <strong>{opponent.name ?? 'a friend'}</strong> · {war.mode}{war.generated && <> · generated Hives ({war.generated.structures})</>}</span>
                            {timing}
                            {nudge && <ShareButton url={warLink(warId)} text={`Your move in Hog of War: round ${war.roundNumber} waits on you.`} label="Send nudge" />}
                        </div>
                        <div className="new-game-row">
                            <button className="rules-btn" onClick={() => { setMenuOpen(false); setRulesOpen(true) }}>
                                <span className="new-game-btn__main">Rules</span>
                            </button>
                            <button className="audio-btn" onClick={toggleSound} aria-pressed={soundOn}
                                aria-label={soundOn ? 'Mute sound' : 'Unmute sound'} title={soundOn ? 'Sound on' : 'Sound off'}>
                                {soundOn ? (
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <path d="M11 5 6 9H2v6h4l5 4z" />
                                        <path d="M15.5 8.5a5 5 0 0 1 0 7" />
                                        <path d="M18.5 5.5a9 9 0 0 1 0 13" />
                                    </svg>
                                ) : (
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <path d="M11 5 6 9H2v6h4l5 4z" />
                                        <line x1="22" y1="9" x2="16" y2="15" />
                                        <line x1="16" y1="9" x2="22" y2="15" />
                                    </svg>
                                )}
                            </button>
                            <button className="new-game-btn" onClick={goHome}>
                                <span className="new-game-btn__main">Home</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </main>
    )
}
